import React, { useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Legend, ChartOptions, scales } from 'chart.js';
import { useGetHiringApplicationsGraphData } from 'src/hooks/campusHiring.hooks';
import { useParams, useSearchParams } from 'react-router-dom';
import { CampusApplicationDocument, SkillGroupType } from 'types';
import { MdOutlineErrorOutline } from 'react-icons/md';
import { Spinner } from 'flowbite-react';
import { getSkillColor } from '../profile/ProfileTechnicalScorecard';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';
import { createCircularImage } from './SkillsMatchGraph';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

type CandidatesData = {
	name: string;
	imageUrl: string;
	imgElement: HTMLCanvasElement;
	passingYear: number;
	profileLinkUrl: string;
	skills: { name: string; color: string }[];
	minSalary: number;
	maxSalary: number;
	level: number;
	position: {
		x: number;
		y: number;
	};
};

const getSalaryRanges = (min: number, max: number) => {
	// Handle edge case where min is 0 or very small
	const effectiveMin = min <= 3 ? 0 : min;
	const belowRange = { min: Math.max(0, effectiveMin - (max - effectiveMin) / 2), max: effectiveMin };
	const matchRange = { min: effectiveMin, max };
	const aboveRange = { min: max, max: max + (max - effectiveMin) / 2 };

	return [belowRange, matchRange, aboveRange];
};

// Generate random position within the appropriate range
const randomizePosition = (min: number, max: number) => {
	return min + Math.random() * (max - min);
};

const SalaryMatchGraph: React.FC = () => {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const top = Number(searchParams.get('top')) ?? 10;
	// Dummy data for candidates
	const [candidates, setCandidates] = useState<CandidatesData[]>([]);
	const [recruiterSalary, setRecruiterSalary] = useState<{ min: number; max: number }>({ min: 0, max: 0 });

	// Step 3: Define chart options with dynamic x-axis based on salary ranges
	const salaryRanges = getSalaryRanges(recruiterSalary.min, recruiterSalary.max);
	const options: ChartOptions<'scatter'> = {
		responsive: true,
		animation: {
			duration: 0, // Disable animations for better performance
		},
		elements: {
			point: {
				radius: 6,
				hitRadius: 22,
				hoverRadius: 24,
				borderWidth: 6,
			},
		},
		scales: {
			x: {
				min:
					candidates.length > 0
						? Math.min(Math.min(...candidates.map(candidate => candidate.minSalary)), salaryRanges[0].min)
						: salaryRanges[0].min,
				max: salaryRanges[2].max,
				title: {
					display: true,
					text: 'Salary Range →',
					color: '#28293D',
					font: {
						size: 12,
						style: 'italic',
						family: 'Inter, sans-serif',
					},
				},
				grid: { display: false },
				ticks: {
					count: 3,
					callback: (value: number | string) => {
						const numValue = Number(value);
						if (numValue <= salaryRanges[0].max) {
							return `${Math.round(salaryRanges[0].min)} - ${Math.round(salaryRanges[0].max)} LPA`;
						}
						if (numValue <= salaryRanges[1].max) {
							return `${Math.round(salaryRanges[1].min)} - ${Math.round(salaryRanges[1].max)} LPA`;
						}
						return `${Math.round(salaryRanges[2].min)} - ${Math.round(salaryRanges[2].max)} LPA`;
					},
					font: { size: 12, family: 'Inter, sans-serif' },
					align: 'center',
					crossAlign: 'center',
				},
				border: { display: true, color: '#28293d8f' },
			},
			y: {
				min: 0,
				title: {
					display: false,
					text: 'Score →',
					color: '#28293D',
					font: {
						size: 12,
						style: 'italic',
						family: 'Inter, sans-serif',
					},
				},
				grid: { display: false },
				ticks: {
					stepSize: 0.9,
					display: false,
					font: { size: 12, family: 'Inter, sans-serif' },
				},
				border: { display: false, color: '#28293d8f' },
			},
		},
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: false,
				external: function (context) {
					const tooltipModel = context.tooltip;

					if (tooltipModel.opacity === 0) {
						// Remove tooltip from DOM when not hovering
						const existingTooltipEl = document.getElementById('chartjs-tooltip');
						if (existingTooltipEl && !existingTooltipEl.matches(':hover')) {
							existingTooltipEl.style.opacity = '0';
							document.body.removeChild(existingTooltipEl);
						}
						return;
					}

					// Create tooltip element if it doesn't exist
					let tooltipEl = document.getElementById('chartjs-tooltip');
					if (!tooltipEl) {
						tooltipEl = document.createElement('div');
						tooltipEl.id = 'chartjs-tooltip';
						tooltipEl.innerHTML = '<div></div>';
						document.body.appendChild(tooltipEl);
					}

					const dataIndex = tooltipModel.dataPoints?.[0]?.dataIndex;

					if (dataIndex === undefined || !candidates[dataIndex]) return;

					const dataPoint = candidates[dataIndex];

					const innerHtml = /* HTML */ `
						<div class="bg-white p-2 rounded-lg shadow-lg border border-[#E4E4EB] max-w-[240px]">
							<div class="flex items-center gap-3 mb-2">
								<img src="${dataPoint.imageUrl}" class="w-10 h-10 rounded-full" />
								<div>
									<div class="font-semibold text-base text-gray-800">${dataPoint.name}</div>
									<div class="text-xs text-gray-500">${dataPoint.minSalary} - ${dataPoint.maxSalary} LPA</div>
								</div>
							</div>
							<div>
								<div class="flex flex-wrap gap-1">
									${dataPoint.skills
										.map(el => {
											return /* HTML */ `<div
												class="text-[#555770] flex items-center gap-1 text-[8.167px] text-center font-[500] leading-[11.667px] border-[0.635px] bg-white border-[#E7E7EE] rounded-[10.162px] px-1"
											>
												<span
													class="rounded-full min-w-[5.385px] min-h-[5.385px]"
													style="background-color: ${el.color || '#555770'}"
												></span>
												${el.name}
											</div>`;
										})
										.join('')}
								</div>
								<div
									class="mt-2 text-xs text-blue-600 cursor-pointer"
									onclick="window.open('${dataPoint.profileLinkUrl}', '_blank')"
								>
									Click to view profile →
								</div>
							</div>
						</div>
					`;

					tooltipEl.querySelector('div')!.innerHTML = innerHtml;

					// Set initial styles
					tooltipEl.style.opacity = '1';
					tooltipEl.style.position = 'fixed';
					tooltipEl.style.pointerEvents = 'auto';
					tooltipEl.style.transition = 'all 0.2s ease';
					tooltipEl.style.zIndex = '1000';

					// Get positions and dimensions
					const position = context.chart.canvas.getBoundingClientRect();
					const tooltipRect = tooltipEl.getBoundingClientRect();
					const chartRect = context.chart.canvas.getBoundingClientRect();

					// Calculate initial positions
					let positionX = position.left + tooltipModel.caretX;
					let positionY = position.top + tooltipModel.caretY;

					// Get available space in each direction
					const spaceAbove = positionY - tooltipRect.height - chartRect.top;
					const spaceBelow = chartRect.bottom - positionY;
					const spaceLeft = positionX - tooltipRect.width / 2 - chartRect.left;
					const spaceRight = chartRect.right - (positionX + tooltipRect.width / 2);

					// Determine best position
					let transform = '';

					// Horizontal positioning
					if (spaceLeft < 0) {
						positionX = chartRect.left + tooltipRect.width / 2 + 10;
					} else if (spaceRight < 0) {
						positionX = chartRect.right - tooltipRect.width / 2 - 10;
					}

					// Vertical positioning
					if (spaceAbove < 0 && spaceBelow > tooltipRect.height) {
						// Show below if not enough space above but enough below
						positionY = positionY + 10;
						transform = 'translate(-50%, 0)';
					} else {
						// Default to above
						transform = 'translate(-50%, -100%)';
						positionY = positionY - 10;
					}

					// Apply positions
					tooltipEl.style.left = `${positionX}px`;
					tooltipEl.style.top = `${positionY}px`;
					tooltipEl.style.transform = transform;

					// Handle mouse leave
					tooltipEl.onmouseleave = () => {
						tooltipEl!.style.opacity = '0';
						tooltipEl!.style.pointerEvents = 'none';
					};
				},
			},
		},
		onClick: (event, elements) => {
			if (elements.length > 0) {
				const index = elements[0].index;
				const candidate = candidates[index];
				window.open(candidate.profileLinkUrl, '_blank');
			}
		},
	};

	// Step 4: Custom plugin for static recruiter salary background shading
	const customPlugin = {
		id: 'customBackground',
		beforeDraw: (chart: any) => {
			// Comprehensive safety checks
			if (!chart?.ctx || !chart?.chartArea || !chart?.data?.datasets?.[0]?.data) {
				console.log('Chart not ready yet', chart);
				return;
			}

			try {
				const { ctx, chartArea } = chart;
				const { top, bottom, left, right, width, height } = chartArea;

				// Additional safety checks for dimensions
				if (!width || !height || !left || !top || !bottom) {
					console.log('Chart dimensions not ready');
					return;
				}

				// Initialize context state
				let contextSaved = false;

				try {
					ctx.save();
					contextSaved = true;

					// Calculate position for the recruiter's salary range
					const startX = chart.scales.x.getPixelForValue(recruiterSalary.min);
					const endX = chart.scales.x.getPixelForValue(recruiterSalary.max);

					// Draw the shaded "Your Salary" range
					ctx.save();
					ctx.fillStyle = 'rgba(183, 194, 255, 0.15)';
					ctx.fillRect(startX, chartArea.top, endX - startX, chartArea.height);

					// Draw dotted border around the shaded area
					ctx.setLineDash([5, 5]);
					ctx.strokeStyle = '#7777E7';
					ctx.lineWidth = 2;
					ctx.strokeRect(startX, chartArea.top, endX - startX, chartArea.height);

					// Label "Your Salary" at the top center
					ctx.fillStyle = '#7777E7';
					ctx.font = 'bold 14px Inter, sans-serif';
					ctx.textAlign = 'center';
					ctx.fillText('Your Salary', startX + (endX - startX) / 2, chartArea.top - 10);
				} finally {
					// Only restore if we successfully saved
					if (contextSaved) {
						try {
							ctx.restore();
						} catch (error) {
							console.error('Error restoring context:', error);
						}
					}
				}
			} catch (error) {
				console.error('Error in column plugin:', error);
			}
		},
	};

	const {
		isLoading: isLoading,
		isError: isApplicationError,
		isFetching: applicationLoading,
	} = useGetHiringApplicationsGraphData({
		page: 0,
		limit: [10, 20, 50].includes(top) ? top : 10,
		hiringAssessment: id || '',
		sort: 'salary',
		onComplete: async data => {
			console.log('oncomplete is working', data);
			setRecruiterSalary({
				min: data?.hiringAssessment?.salaryRange?.min,
				max: data?.hiringAssessment?.salaryRange?.max,
			});
			const candidatesData = await Promise.all(
				data?.campusHiringApplications?.map(async ele => {
					const candidateSalary = {
						min: (ele.application as CampusApplicationDocument)?.predictedSalary?.min ?? 0,
						max: (ele.application as CampusApplicationDocument)?.predictedSalary?.max ?? 0,
					};

					// Find which salary range this candidate belongs to
					const salaryRanges = getSalaryRanges(
						data?.hiringAssessment?.salaryRange?.min ?? 0,
						data?.hiringAssessment?.salaryRange?.max ?? 0,
					);

					// Determine which range the candidate falls into
					const candidateRange = salaryRanges.find(
						range => candidateSalary.min >= range.min && candidateSalary.min <= range.max,
					);

					// Get random X within the appropriate salary range
					const randomX = candidateRange
						? randomizePosition(candidateRange.min, candidateRange.max)
						: candidateSalary.min;

					// Get random Y while maintaining relative level position
					const baseLevel = (ele.application as CampusApplicationDocument)?.level ?? 0;
					const randomY = baseLevel + (Math.random() * 0.5 - 0.25); // ±0.25 variation
					const img =
						ele?.profile?.proctoringImageUrl ||
						'https://masai-website-images.s3.ap-south-1.amazonaws.com/svgviewer_png_output_26_b364f6620b.png';
					const imgElement = await createCircularImage(img); // Wait for the image to load

					return {
						name: ele.name,
						imageUrl: ele.profile.proctoringImageUrl || '',
						imgElement: imgElement,
						passingYear: ele.profile.gradYear ? Number(ele.profile.gradYear) : 0,
						profileLinkUrl: `/profile/${ele.handler}`,
						skills:
							(ele.application as CampusApplicationDocument)?.skillRatings?.map(item => ({
								name: item.skill.name,
								color: getSkillColor(item.skill.skillGroupType as SkillGroupType),
							})) || [],
						minSalary: candidateSalary.min,
						maxSalary: candidateSalary.max,
						level: baseLevel,
						position: {
							x: randomX,
							y: Math.max(0, randomY), // Ensure Y doesn't go below 0
						},
					};
				}),
			);
			console.log('candidatesData:', candidatesData);
			setCandidates(candidatesData || []);
		},
	});

	if (isLoading || applicationLoading) {
		return (
			<div className="mt-[22px] flex items-center justify-center h-[50vh]">
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto" />
			</div>
		);
	}

	return (
		<div className="w-full mx-auto px-4 py-4 rounded-md bg-white relative">
			{applicationLoading || isLoading ? (
				<div className="absolute top-2 md:top-4 right-2 md:right-4 max-h-[24px] max-w-[24px] h-[19px] w-[19px]">
					<Spinner size="20px" className="mt-[-2px]" />
				</div>
			) : isApplicationError ? (
				<div className="absolute top-2 md:top-4 right-2 md:right-4 max-h-[24px] max-w-[24px] h-[19px] w-[19px]">
					<MdOutlineErrorOutline color="red" />
				</div>
			) : null}
			<div className="w-full">
				<Scatter
					data={{
						labels: ['Candidate Analysis'],
						datasets: [
							{
								pointRadius: 10,
								pointStyle: candidates?.map(candidate => candidate?.imgElement) || [],
								label: 'Candidate Analysis',
								data: candidates.map(candidate => candidate?.position) || [],
								clip: false,
							},
						],
					}}
					options={options}
					plugins={[customPlugin]}
				/>
			</div>
		</div>
	);
};

export default SalaryMatchGraph;
